import React, { useRef, useState } from "react";
import { Box, Button, Typography, IconButton, Tooltip } from "@mui/material";
import { CloudUpload as UploadIcon, Close as CloseIcon } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { theme } from "../../theme";

interface BrandLogoUploadProps {
    onFileSelected: (file: File | null) => void;
    currentImage?: {
        url: string;
        name: string;
    };
    disabled?: boolean;
}

const MAX_FILE_SIZE_MB = 5;

export const BrandLogoUpload: React.FC<BrandLogoUploadProps> = ({
    onFileSelected,
    currentImage,
    disabled = false
}) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { t } = useTranslation();
    const [dragActive, setDragActive] = useState(false);

    const handleFileInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (validateFile(file)) {
                onFileSelected(file);
            }
        }

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const validateFile = (file: File): boolean => {
        const allowedTypes = ["image/png", "image/svg+xml", "image/jpeg", "image/jpg"];
        if (!allowedTypes.includes(file.type)) {
            alert(t("brand.upload.invalidType"));
            return false;
        }

        const maxSize = MAX_FILE_SIZE_MB * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            alert(t("brand.upload.fileTooLarge", { maxSize: MAX_FILE_SIZE_MB }));
            return false;
        }

        return true;
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setDragActive(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
    };

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);

        if (disabled) return;

        const files = e.dataTransfer.files;
        if (files && files.length > 0) {
            const file = files[0];
            if (validateFile(file)) {
                onFileSelected(file);
            }
        }
    };

    const handleClick = () => {
        if (!disabled) {
            fileInputRef.current?.click();
        }
    };

    const handleRemoveImage = () => {
        onFileSelected(null);
    };

    return (
        <>
            <input
                type="file"
                accept=".png,.svg,.jpg,.jpeg,image/png,image/svg+xml,image/jpeg"
                style={{ display: "none" }}
                ref={fileInputRef}
                onChange={handleFileInput}
                disabled={disabled}
            />

            {currentImage ? (
                <Box
                    sx={{
                        border: "1px solid #e0e0e0",
                        borderRadius: 1,
                        p: 2,
                        textAlign: "center",
                        position: "relative",
                        bgcolor: "rgba(248, 249, 250, 0.8)"
                    }}
                    data-testid="brand-logo-preview"
                >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            component="img"
                            src={currentImage.url}
                            alt="Brand logo"
                            sx={{
                                width: 64,
                                height: 64,
                                objectFit: "contain",
                                borderRadius: 1,
                                border: "1px solid #e0e0e0"
                            }}
                        />
                        <Box sx={{ flex: 1, textAlign: "left" }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {currentImage.name}
                            </Typography>
                        </Box>
                        {!disabled && (
                            <Tooltip title={t("brand.actions.removeLogo")}>
                                <IconButton
                                    onClick={handleRemoveImage}
                                    size="small"
                                    sx={{
                                        color: "#6b7280",
                                        "&:hover": {
                                            color: "#ef4444",
                                            bgcolor: "rgba(239, 68, 68, 0.1)"
                                        }
                                    }}
                                    data-testid="remove-brand-logo-button"
                                >
                                    <CloseIcon sx={{ fontSize: 18 }} />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                </Box>
            ) : (
                <Box
                    sx={{
                        border: `1px dashed ${dragActive ? theme.palette.brand.main : "#ccc"}`,
                        borderRadius: 1,
                        p: 3,
                        textAlign: "center",
                        bgcolor: dragActive ? "rgba(235, 48, 79, 0.04)" : "rgba(248, 249, 250, 0.8)",
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: disabled ? 0.6 : 1,
                        transition: "all 0.2s ease",
                        "&:hover": !disabled ? {
                            bgcolor: "rgba(235, 48, 79, 0.04)",
                            borderColor: theme.palette.brand.main
                        } : {}
                    }}
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={handleClick}
                    data-testid="brand-logo-upload-area"
                >
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                        <UploadIcon
                            sx={{
                                fontSize: 28,
                                color: dragActive ? theme.palette.brand.main : "#666",
                                transition: "color 0.2s ease"
                            }}
                        />
                        <Box sx={{ textAlign: "left" }}>
                            <Typography
                                variant="body1"
                                sx={{
                                    fontWeight: 500,
                                    color: dragActive ? theme.palette.brand.main : "inherit",
                                    mb: 0.5
                                }}
                            >
                                {dragActive ? t("brand.upload.dropHere") : t("brand.upload.dropOrBrowse")}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {t("brand.upload.maxSize", { maxSize: MAX_FILE_SIZE_MB })}
                            </Typography>
                        </Box>
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={disabled}
                            sx={{
                                borderColor: theme.palette.brand.main,
                                color: theme.palette.brand.main,
                                fontSize: "0.75rem",
                                "&:hover": {
                                    borderColor: "#b91c1c",
                                    bgcolor: "rgba(235, 48, 79, 0.04)"
                                },
                                "&:disabled": {
                                    borderColor: "#ccc",
                                    color: "#999"
                                }
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                fileInputRef.current?.click();
                            }}
                            data-testid="browse-brand-logo-button"
                        >
                            {t("brand.upload.browse")}
                        </Button>
                    </Box>
                </Box>
            )}
        </>
    );
};